import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil, faSave, faPlus, faTrash,
} from '@fortawesome/free-solid-svg-icons';
/* eslint-disable import/no-extraneous-dependencies */
import Modal from 'react-modal';
import { MultiSelect } from 'react-multi-select-component';
import useFetch from '../../hooks/useFetch';
import EventCard from './EventCard';
import { useUserData } from '../../providers/UserDataProvider';

function ManageEventSeries() {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    fetchData, loading, error, serverError,
  } = useFetch();
  const { fetchData: fetchModalData } = useFetch();
  const { userData } = useUserData();
  const [eventSeries, setEventSeries] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [createCollectionModalIsOpen, setCreateCollectionModalIsOpen] = useState(false);
  const [collectionName, setCollectionName] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventSeriesNameEdit, setEventSeriesNameEdit] = useState();
  const [leaderboardConfigIdEdit, setLeaderboardConfigIdEdit] = useState('');
  const [addEventModalIsOpen, setAddEventModalIsOpen] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEventsToAdd, setSelectedEventsToAdd] = useState([]);
  const [addEventTab, setAddEventTab] = useState('existing');
  const [users, setUsers] = useState([]);
  const [leaderboards, setLeaderboards] = useState([]);
  const [newEvents, setNewEvents] = useState([{
    name: '',
    startAt: '',
    endAt: '',
    leaderboard_config_id: '',
    participants: [],
  }]);
  const [sameParticipants, setSameParticipants] = useState(false);
  const [globalParticipants, setGlobalParticipants] = useState([]);
  const [activeView, setActiveView] = useState('uncategorized');
  const [removeModalIsOpen, setRemoveModalIsOpen] = useState(false);
  const [eventToRemove, setEventToRemove] = useState(null);
  const [collectionMode, setCollectionMode] = useState('new');
  const [selectedCollectionId, setSelectedCollectionId] = useState('');

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    const fetchEventSeries = async () => {
      const data = await fetchData(`/api/events/getBySeriesId/${id}`);
      const leaderboardsData = await fetchData('/api/dropdowns/leaderboards');
      setLeaderboards(leaderboardsData || []);
      if (data) {
        const seriesData = Array.isArray(data) ? data[0] : data;
        setEventSeries({
          ...seriesData,
          name: seriesData.name || seriesData.event_series_name,
        });
      }
    };

    fetchEventSeries();
  }, [id, fetchData]);

  const openModal = () => {
    setEventSeriesNameEdit(eventSeries.name);
    setLeaderboardConfigIdEdit(eventSeries.leaderboard_config_id || '');
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleSetEventSeries = (updatedEventSeries) => {
    const savedEventSeries = {
      ...eventSeries,
      name: updatedEventSeries.name,
      leaderboard_config_id: updatedEventSeries.leaderboard_config_id,
    };
    setEventSeries(savedEventSeries);
  };

  const handleSaveEventSeries = async () => {
    const data = await fetchData(`/api/events/update-event-series/${id}`, {
      method: 'POST',
      body: {
        event_series_name: eventSeriesNameEdit,
        leaderboard_config_id: leaderboardConfigIdEdit,
      },
    });
    if (data) {
      const updatedData = Array.isArray(data) ? data[0] : data;
      const normalizedUpdate = {
        ...updatedData,
        name: updatedData.name || updatedData.event_series_name,
      };
      handleSetEventSeries(normalizedUpdate);
    }
    closeModal();
  };

  const handleSelectEvent = (eventId) => {
    setSelectedEvents((prevSelectedEvents) => {
      if (prevSelectedEvents.includes(eventId)) {
        return prevSelectedEvents.filter((existingId) => existingId !== eventId);
      }
      return [...prevSelectedEvents, eventId];
    });
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setSelectedEvents([]);
  };

  const refreshSeries = async () => {
    const data = await fetchData(`/api/events/getBySeriesId/${id}`);
    if (data) {
      const seriesData = Array.isArray(data) ? data[0] : data;
      setEventSeries({
        ...seriesData,
        name: seriesData.name || seriesData.event_series_name,
      });
    }
  };

  const handleRemoveEventFromSeries = async (eventId) => {
    if (activeView === 'uncategorized') {
      // eslint-disable-next-line no-alert
      if (window.confirm('Are you sure you want to remove this event from the series?')) {
        const eventData = await fetchData(`/api/events/getById/${eventId}`);
        if (eventData) {
          const updatedEvent = {
            ...eventData,
            event_series_id: null,
            participants: eventData.participantUsers
              ? eventData.participantUsers.map((p) => p.id)
              : [],
          };
          await fetchData(`/api/events/update/${eventId}`, { method: 'PUT', body: updatedEvent });
          await refreshSeries();
        }
      }
    } else {
      setEventToRemove(eventId);
      setRemoveModalIsOpen(true);
    }
  };

  const handleConfirmRemoveFromSeries = async () => {
    if (eventToRemove) {
      const eventId = eventToRemove;
      const eventData = await fetchData(`/api/events/getById/${eventId}`);
      if (eventData) {
        const updatedEvent = {
          ...eventData,
          event_series_id: null,
          event_series_collection_id: null,
          participants: eventData.participantUsers
            ? eventData.participantUsers.map((p) => p.id)
            : [],
        };
        await fetchData(`/api/events/update/${eventId}`, { method: 'PUT', body: updatedEvent });
        await refreshSeries();
      }
      setRemoveModalIsOpen(false);
      setEventToRemove(null);
    }
  };

  const handleConfirmRemoveFromCollection = async () => {
    if (eventToRemove) {
      const eventId = eventToRemove;
      const eventData = await fetchData(`/api/events/getById/${eventId}`);
      if (eventData) {
        const updatedEvent = {
          ...eventData,
          event_series_id: eventData.eventSeries?.id || eventData.event_series_id,
          event_series_collection_id: null,
          participants: eventData.participantUsers
            ? eventData.participantUsers.map((p) => p.id)
            : [],
        };
        await fetchData(`/api/events/update/${eventId}`, { method: 'PUT', body: updatedEvent });
        await refreshSeries();
      }
      setRemoveModalIsOpen(false);
      setEventToRemove(null);
    }
  };

  const handleDeleteSeries = async () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete this event series? This will remove all events from the series and delete all collections within it.')) {
      const response = await fetchData(`/api/events/delete-series/${id}`, { method: 'DELETE' });
      if (response) {
        navigate('/my-events');
      }
    }
  };

  const openCreateCollectionModal = () => {
    setCollectionMode('new');
    setCollectionName('');
    setSelectedCollectionId('');
    setCreateCollectionModalIsOpen(true);
  };

  const closeCreateCollectionModal = () => {
    setCreateCollectionModalIsOpen(false);
  };

  const handleAddToCollection = async () => {
    if (collectionMode === 'new') {
      const data = await fetchData('/api/events/series/collection/create', {
        method: 'POST',
        body: {
          name: collectionName,
          event_series_id: id,
          event_ids: selectedEvents,
        },
      });
      if (data) {
        await refreshSeries();
        closeCreateCollectionModal();
        setSelectedEvents([]);
        setCollectionName('');
      }
    } else if (selectedCollectionId) {
      const updates = selectedEvents.map(async (eventId) => {
        const eventData = await fetchData(`/api/events/getById/${eventId}`);
        if (eventData) {
          const updatedEvent = {
            ...eventData,
            event_series_collection_id: selectedCollectionId,
            participants: eventData.participantUsers
              ? eventData.participantUsers.map((p) => p.id)
              : [],
          };
          // Ensure series ID is preserved
          updatedEvent.event_series_id = eventData.eventSeries?.id || eventData.event_series_id;

          return fetchData(`/api/events/update/${eventId}`, { method: 'PUT', body: updatedEvent });
        }
        return Promise.resolve();
      });

      await Promise.all(updates);
      await refreshSeries();
      closeCreateCollectionModal();
      setSelectedEvents([]);
      setSelectedCollectionId('');
    }
  };

  const openAddEventModal = async () => {
    const allEventsData = await fetchModalData('/api/events/getAll');
    const usersData = await fetchModalData('/api/dropdowns/users');
    const leaderboardsData = await fetchModalData('/api/dropdowns/leaderboards');

    if (allEventsData && allEventsData.events) {
      const currentSeriesId = Number(id);
      const filtered = allEventsData.events.filter(
        (e) => (
          e.event_series_id || e.eventSeries?.id
        ) !== currentSeriesId && e.createdBy === userData.id,
      );
      setAvailableEvents(filtered.map((e) => ({ label: e.name, value: e.id })));
    }
    setUsers(usersData?.usersWithLabel || []);
    setLeaderboards(leaderboardsData || []);
    setNewEvents([{
      name: '',
      startAt: '',
      endAt: '',
      leaderboard_config_id: eventSeries?.leaderboard_config_id || '',
      participants: [],
    }]);
    setAddEventModalIsOpen(true);
  };

  const closeAddEventModal = () => {
    setAddEventModalIsOpen(false);
    setSelectedEventsToAdd([]);
    setAddEventTab('existing');
    setNewEvents([{
      name: '',
      startAt: '',
      endAt: '',
      leaderboard_config_id: eventSeries?.leaderboard_config_id || '',
      participants: [],
    }]);
    setSameParticipants(false);
    setGlobalParticipants([]);
  };

  const handleAddEvents = async () => {
    const updates = selectedEventsToAdd.map(async (selection) => {
      const eventId = selection.value;
      const eventData = await fetchModalData(`/api/events/getById/${eventId}`);
      if (eventData) {
        const participantIds = eventData.participantUsers
          ? eventData.participantUsers.map((p) => p.id)
          : [];
        const updatedEvent = {
          ...eventData,
          event_series_id: id,
          participants: participantIds,
        };
        return fetchModalData(`/api/events/update/${eventId}`, { method: 'PUT', body: updatedEvent });
      }
      return Promise.resolve();
    });

    await Promise.all(updates);

    await refreshSeries();
    closeAddEventModal();
  };

  const handleNewEventChange = (index, field, value) => {
    const updatedEvents = [...newEvents];
    const updatedEvent = { ...updatedEvents[index] };

    if (field === 'startAt') {
      updatedEvent.startAt = value;
      if (!updatedEvent.endAt || updatedEvent.endAt < value) {
        updatedEvent.endAt = value;
      }
    } else if (field === 'endAt') {
      updatedEvent.endAt = (updatedEvent.startAt && value < updatedEvent.startAt)
        ? updatedEvent.startAt
        : value;
    } else {
      updatedEvent[field] = value;
    }
    updatedEvents[index] = updatedEvent;
    setNewEvents(updatedEvents);
  };

  const addNewEventRow = () => {
    setNewEvents([...newEvents, {
      name: '',
      startAt: '',
      endAt: '',
      leaderboard_config_id: eventSeries?.leaderboard_config_id || '',
      participants: [],
    }]);
  };

  const removeEventRow = (index) => {
    const updatedEvents = newEvents.filter((_, i) => i !== index);
    setNewEvents(updatedEvents);
  };

  const handleCreateNewEvent = async (e) => {
    e.preventDefault();
    const eventsPayload = newEvents.map((event) => ({
      ...event,
      participants: (
        sameParticipants ? globalParticipants : event.participants
      ).map((p) => p.value),
    }));

    const payload = {
      event_series_id: id,
      events: eventsPayload,
    };

    const response = await fetchModalData('/api/events/addEventsToSeries', { method: 'POST', body: payload });

    if (response) {
      await refreshSeries();
      closeAddEventModal();
    }
  };

  const customStyles = {
    content: {
      top: '50%',
      left: '60%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '80%', // Adjust this value to change the width of the modal
      maxWidth: '750px', // Set a max-width if needed
      minHeight: '750px',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)', // This will darken the background
    },
  };

  const smallModalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '500px',
      maxWidth: '90%',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
    },
  };

  if (loading) return <div>Loading...</div>;
  if (serverError) return serverError;
  if (error) return <div>Error: {error.message || error.error}</div>;
  if (!eventSeries) return <div>No event series found.</div>;

  const collections = eventSeries.eventSeriesCollections
    || eventSeries.event_series_collections
    || [];
  const eventsToDisplay = activeView === 'uncategorized'
    ? (eventSeries.events || [])
    : (collections.find((c) => c.id === activeView)?.events || []);

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-row justify-between mb-4">
        <h1 className="text-2xl font-bold mb-4 text-white">Manage Event Series: {eventSeries.name}</h1>
        <div className="flex flex-row">
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded"
            onClick={() => navigate(`/my-events/view-series-leaderboard/${activeView === 'uncategorized' ? id : activeView}`, { state: { type: activeView === 'uncategorized' ? 'series' : 'collection' } })}
            title={activeView === 'uncategorized' ? 'View Series Leaderboards' : 'View Collection Leaderboard'}
          >
            {activeView === 'uncategorized' ? 'View Series Leaderboard' : 'View Collection Leaderboard'}
          </button>
          <button
            type="button"
            className="ml-2 bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded"
            onClick={openAddEventModal}
            title="Add Events to Series"
          >
            Add Events <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            type="button"
            className="ml-2 bg-amber-500 hover:bg-amber-700 text-white font-bold py-1 px-3 rounded"
            onClick={openModal}
            title="Edit Event Series Details"
          >
            Edit Series <FontAwesomeIcon icon={faPencil} />
          </button>
          <button
            type="button"
            className="ml-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded"
            onClick={handleDeleteSeries}
            title="Delete Event Series"
          >
            Delete Series <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Edit Event Series"
        style={smallModalStyles}
      >
        <form>
          <div className="mb-4">
            <label htmlFor="event_series_name" className="block text-gray-700 text-sm font-bold mb-2">Event Series Name
              <input
                value={eventSeriesNameEdit}
                type="text"
                id="event_series_name"
                name="event_series_name"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-light focus:outline-none focus:shadow-outline"
                onChange={(e) => setEventSeriesNameEdit(e.target.value)}
              />
            </label>
          </div>
          <div className="mb-4">
            <label htmlFor="leaderboard_config_id" className="block text-gray-700 text-sm font-bold mb-2">Series Leaderboard Config
              <select
                id="leaderboard_config_id"
                value={leaderboardConfigIdEdit}
                onChange={(e) => setLeaderboardConfigIdEdit(e.target.value)}
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Select Config</option>
                {leaderboards.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </label>
          </div>
        </form>
        <div className="flex justify-between">
          <button type="button" className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded" onClick={closeModal}>Close</button>
          <button
            type="button"
            className="bg-amber-500 hover:bg-amber-700 text-white font-bold py-1 px-3 rounded"
            onClick={handleSaveEventSeries}
          >
            Save <FontAwesomeIcon icon={faSave} />
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={createCollectionModalIsOpen}
        onRequestClose={closeCreateCollectionModal}
        contentLabel="Add to Collection"
        style={smallModalStyles}
      >
        <h2 className="text-xl font-bold mb-4 text-black">Add to Collection</h2>
        <div className="flex border-b border-gray-200 mb-4">
          <button
            type="button"
            className={`py-2 px-4 focus:outline-none ${collectionMode === 'new' ? 'border-b-2 border-blue-500 text-blue-500 font-bold' : 'text-gray-500'}`}
            onClick={() => setCollectionMode('new')}
          >
            Create New
          </button>
          <button
            type="button"
            className={`py-2 px-4 focus:outline-none ${collectionMode === 'existing' ? 'border-b-2 border-blue-500 text-blue-500 font-bold' : 'text-gray-500'}`}
            onClick={() => setCollectionMode('existing')}
          >
            Add to Existing
          </button>
        </div>
        <form>
          {collectionMode === 'new' ? (
            <div className="mb-4">
              <label htmlFor="collection_name" className="block text-gray-700 text-sm font-bold mb-2">Collection Name
                <input
                  value={collectionName}
                  type="text"
                  id="collection_name"
                  name="collection_name"
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-light focus:outline-none focus:shadow-outline"
                  onChange={(e) => setCollectionName(e.target.value)}
                />
              </label>
            </div>
          ) : (
            <div className="mb-4">
              <label htmlFor="existing_collection" className="block text-gray-700 text-sm font-bold mb-2">Select Collection
                <select
                  id="existing_collection"
                  value={selectedCollectionId}
                  onChange={(e) => setSelectedCollectionId(e.target.value)}
                  className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                >
                  <option value="">Select a collection...</option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </form>
        <div className="flex justify-between">
          <button type="button" className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded" onClick={closeCreateCollectionModal}>Close</button>
          <button
            type="button"
            className="bg-amber-500 hover:bg-amber-700 text-white font-bold py-1 px-3 rounded"
            onClick={handleAddToCollection}
          >
            {collectionMode === 'new' ? 'Create' : 'Add'}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={removeModalIsOpen}
        onRequestClose={() => setRemoveModalIsOpen(false)}
        contentLabel="Remove Event"
        style={smallModalStyles}
      >
        <h2 className="text-xl font-bold mb-4 text-black">Remove Event</h2>
        <p className="mb-4 text-black">Do you want to remove this event from the collection only, or from the entire series?</p>
        <div className="flex justify-between gap-2">
          <button type="button" className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded" onClick={() => setRemoveModalIsOpen(false)}>Cancel</button>
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded"
            onClick={handleConfirmRemoveFromCollection}
          >
            Remove from Collection
          </button>
          <button type="button" className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded" onClick={handleConfirmRemoveFromSeries}>
            Remove from Series
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={addEventModalIsOpen}
        onRequestClose={closeAddEventModal}
        contentLabel="Add Events to Series"
        style={customStyles}
      >
        <h2 className="text-xl font-bold mb-4 text-black">Add Events to Series</h2>
        <div className="flex border-b border-gray-200 mb-4">
          <button
            type="button"
            className={`py-2 px-4 focus:outline-none ${addEventTab === 'existing' ? 'border-b-2 border-blue-500 text-blue-500 font-bold' : 'text-gray-500'}`}
            onClick={() => setAddEventTab('existing')}
          >
            Add Existing
          </button>
          <button
            type="button"
            className={`py-2 px-4 focus:outline-none ${addEventTab === 'new' ? 'border-b-2 border-blue-500 text-blue-500 font-bold' : 'text-gray-500'}`}
            onClick={() => setAddEventTab('new')}
          >
            Create New
          </button>
        </div>

        {addEventTab === 'existing' ? (
          <>
            <div className="mb-4">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="block text-gray-700 text-sm font-bold mb-2">Select Events
                <MultiSelect
                  options={availableEvents}
                  value={selectedEventsToAdd}
                  onChange={setSelectedEventsToAdd}
                  labelledBy="Select Events"
                />
              </label>
            </div>
            <div className="flex justify-between">
              <button type="button" className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded" onClick={closeAddEventModal}>Close</button>
              <button type="button" className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded" onClick={handleAddEvents}>
                Add Selected Events
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleCreateNewEvent}>
            <div className="mb-4">
              <label htmlFor="sameParticipants" className="flex items-center space-x-2">
                <input
                  id="sameParticipants"
                  type="checkbox"
                  checked={sameParticipants}
                  onChange={(e) => setSameParticipants(e.target.checked)}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <span className="text-gray-700 font-bold">Same participants for all events?</span>
              </label>
            </div>

            {sameParticipants && (
              <div className="mb-4">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="block text-gray-700 text-sm font-bold mb-2">Global Participants
                  <MultiSelect
                    options={users}
                    value={globalParticipants}
                    onChange={setGlobalParticipants}
                    labelledBy="Select Global Participants"
                  />
                </label>
              </div>
            )}
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {newEvents.map((event, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="mb-6 border-b border-gray-300 pb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-lg">Event {index + 1}</h3>
                    {newEvents.length > 1 && (
                      <button type="button" onClick={() => removeEventRow(index)} className="text-red-500 hover:text-red-700">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-row gap-4 mb-4">
                    <div className="w-1/2">
                      <label htmlFor={`newEventName-${index}`} className="block text-gray-700 text-sm font-bold mb-2">Event Name
                        <input
                          type="text"
                          id={`newEventName-${index}`}
                          value={event.name}
                          onChange={(e) => handleNewEventChange(index, 'name', e.target.value)}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          required
                        />
                      </label>
                    </div>
                    <div className="w-1/2">
                      <label htmlFor={`newEventLeaderboard-${index}`} className="block text-gray-700 text-sm font-bold mb-2">Leaderboard Config
                        <select
                          id={`newEventLeaderboard-${index}`}
                          value={event.leaderboard_config_id}
                          onChange={(e) => handleNewEventChange(index, 'leaderboard_config_id', e.target.value)}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          required
                        >
                          <option value="">Select Config</option>
                          {
                            leaderboards.map(
                              (l) => <option key={l.value} value={l.value}>{l.label}</option>,
                            )
                          }
                        </select>
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 mb-4">
                    <div className="w-1/2">
                      <label htmlFor={`newEventStart-${index}`} className="block text-gray-700 text-sm font-bold mb-2">Start Date
                        <input
                          type="date"
                          id={`newEventStart-${index}`}
                          value={event.startAt}
                          min={minDate}
                          onChange={(e) => handleNewEventChange(index, 'startAt', e.target.value)}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          required
                        />
                      </label>
                    </div>
                    <div className="w-1/2">
                      <label htmlFor={`newEventEnd-${index}`} className="block text-gray-700 text-sm font-bold mb-2">End Date
                        <input
                          type="date"
                          id={`newEventEnd-${index}`}
                          value={event.endAt}
                          min={event.startAt}
                          onChange={(e) => handleNewEventChange(index, 'endAt', e.target.value)}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          required
                        />
                      </label>
                    </div>
                  </div>
                  {!sameParticipants && (
                  <div className="mb-4">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="block text-gray-700 text-sm font-bold mb-2">Participants
                      <MultiSelect
                        options={users}
                        value={event.participants}
                        onChange={(selected) => handleNewEventChange(index, 'participants', selected)}
                        labelledBy="Select Participants"
                      />
                    </label>
                  </div>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addNewEventRow} className="bg-blue-500 text-white px-3 py-1 rounded mb-4">
              Add Another Event <FontAwesomeIcon icon={faPlus} />
            </button>
            <div className="flex justify-between">
              <button type="button" className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-1 px-3 rounded" onClick={closeAddEventModal}>Close</button>
              <button type="submit" className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded">
                Create & Add Events
              </button>
            </div>
          </form>
        )}
      </Modal>
      <div className="flex flex-row justify-between">
        <div className="w-2/3 bg-gray-600 border p-4 rounded-md shadow mr-1">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">
              {activeView === 'uncategorized' ? 'Uncategorized Events' : collections.find((c) => c.id === activeView)?.name}
            </h2>
            {activeView === 'uncategorized' && (
            <button
              type="button"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded disabled:opacity-50"
              onClick={openCreateCollectionModal}
              disabled={selectedEvents.length === 0}
            >
              Add to Collection
            </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {eventsToDisplay.map((event) => (
              <EventCard
                key={event.id || event.event_id}
                event={event}
                selectedEvents={selectedEvents}
                handleSelectEvent={handleSelectEvent}
                onRemove={handleRemoveEventFromSeries}
              />
            ))}
          </div>
        </div>
        <div className="w-1/3 bg-gray-600 border p-4 rounded-md shadow">
          <h2 className="text-lg font-semibold mb-4 text-white">Event Series Collections</h2>
          <div
            className={`flex justify-between items-center cursor-pointer p-2 rounded mb-2 ${activeView === 'uncategorized' ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-500'}`}
            onClick={() => handleViewChange('uncategorized')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleViewChange('uncategorized');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <h3 className="text-md font-semibold text-white">Uncategorized Events</h3>
          </div>
          {collections.map((collection) => (
            <div key={collection.id} className="mb-4">
              <div
                className={`flex justify-between items-center cursor-pointer p-2 rounded hover:bg-gray-500 ${activeView === collection.id ? 'bg-blue-600' : 'bg-gray-700'}`}
                onClick={() => handleViewChange(collection.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleViewChange(collection.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <h3 className="text-md font-semibold text-white">{collection.name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ManageEventSeries;
